/**
 * OnboardingLayout - Layout wrapper for onboarding routes
 *
 * This layout is for new users going through initial onboarding.
 * It requires authentication but does not check for existing provider/shop records.
 */

import type { FC } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { LoadingScreen } from '@/shared/ui/loading';
import { logger } from '@/shared/utils/logger';
import { paths } from '../../builders/paths';

/**
 * OnboardingLayout Component
 *
 * Simple auth check - just ensures user is authenticated.
 * The onboarding pages themselves determine what to show.
 */
export const OnboardingLayout: FC = () => {
  const { isLoading: auth0Loading, isAuthenticated } = useAuth0();

  // Loading state
  if (auth0Loading) {
    return <LoadingScreen message="Loading..." />;
  }

  // Not authenticated - redirect to home
  if (!isAuthenticated) {
    logger.warn('OnboardingLayout: Not authenticated, redirecting to home');
    return <Navigate to={paths.home()} replace />;
  }

  // Authenticated - render children
  return <Outlet />;
};
